import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';

import { useEventLogging } from '@innovamat/event-logging';
import {
  AnnouncementPanel,
  Modal,
  Select,
  Typography,
} from '@innovamat/glimmer-components';
import type { Classroom } from '@innovamat/glow-api-client';
import { dates } from '@innovamat/radiance-utils';

import { useRegionParameters } from '../contents';
import useModifyDigitalPracticeModal from './use-modify-digital-practice-modal';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Content = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;

  > div {
    flex: 1;
  }
`;

type Props = {
  classroom: Classroom | undefined;
  isOpen: boolean;
  onClose: () => void;
  onConfigurationChange?: () => void;
};

const ModifyDigitalPracticeModal: React.FC<Props> = ({
  classroom,
  isOpen,
  onClose,
  onConfigurationChange,
}) => {
  const { t } = useTranslation();
  const { setEventData } = useEventLogging();
  const { regionParameters } = useRegionParameters({});

  const {
    configuredDate,
    daysOfWeek,
    isPending,
    nextOccurrence,
    hasChanges,
    formatDate,
    formatTime,
    handleOnChangeSelect,
    handleOnClose,
    handleOnSave,
    currentWeekPeriod,
    timezone,
  } = useModifyDigitalPracticeModal({
    classroom,
    onClose,
    onConfigurationChange,
  });

  const startDateTime = dates.parseDateTime(
    currentWeekPeriod?.start.date,
    currentWeekPeriod?.start.timezone
  );
  const endDateTime = dates.parseDateTime(
    currentWeekPeriod?.end.date,
    currentWeekPeriod?.end.timezone
  );

  const startDate = startDateTime ? startDateTime.toJSDate() : '';
  const endDate = endDateTime ? endDateTime.toJSDate() : '';

  const startTime = startDateTime
    ? formatTime({
        date: startDateTime.toJSDate(),
        clock: regionParameters?.clock,
        timezone,
      })
    : '';
  const endTime = endDateTime
    ? formatTime({
        date: endDateTime.toJSDate(),
        clock: regionParameters?.clock,
        timezone,
      })
    : '';

  return (
    <Modal
      title={t('digitalPractice.modifyDigitalPracticeModal.title')}
      buttons={[
        {
          children: t('common.save'),
          disabled: !hasChanges || isPending,
          loading: isPending,
          variant: 'accent',
          onClick: () => {
            handleOnSave();
            setEventData('teacher_control_modify_date_start_practice', {});
          },
        },
        {
          children: t('common.cancel'),
          variant: 'tertiary',
          onClick: handleOnClose,
        },
      ]}
      isOpen={isOpen}
      modalWidth={700}
      semantic={{
        type: 'warning',
        text: t('common.modal.important'),
      }}
      closeButton="inner"
      onClose={handleOnClose}
      zIndex={35}
    >
      <ModalContent>
        <Typography.Body1>
          {t('digitalPractice.modifyDigitalPracticeModal.description')}
        </Typography.Body1>
        <Content>
          <Select
            value={configuredDate.digitalPracticeFirstDayOfWeek}
            labelText={t(
              'digitalPractice.modifyDigitalPracticeModal.firstDayOfWeek'
            )}
            options={daysOfWeek}
            menuPortalTarget={document.body}
            onChange={(day) =>
              handleOnChangeSelect(day?.value, 'digitalPracticeFirstDayOfWeek')
            }
          />
        </Content>
        {hasChanges ? (
          <AnnouncementPanel
            text={
              <>
                <Trans
                  i18nKey="digitalPractice.modifyDigitalPracticeModal.informativeText3"
                  components={{
                    strong: <strong />,
                  }}
                  values={{
                    day: `<strong>${
                      nextOccurrence ? formatDate(nextOccurrence) : ''
                    }</strong>`,
                    hour: `<strong>${formatTime({
                      clock: regionParameters?.clock,
                      timezone,
                    })}</strong>`,
                  }}
                />{' '}
                {t(
                  'digitalPractice.modifyDigitalPracticeModal.informativeText4'
                )}
              </>
            }
            canClose={false}
            type="info"
          />
        ) : (
          <AnnouncementPanel
            text={
              <>
                <Trans
                  i18nKey="digitalPractice.modifyDigitalPracticeModal.informativeText1"
                  components={{
                    strong: <strong />,
                  }}
                  values={{
                    day1: `<strong>${
                      startDate && formatDate(startDate)
                    }</strong>`,
                    hour1: `<strong>${startTime}</strong>`,
                    day2: `<strong>${endDate && formatDate(endDate)}</strong>`,
                    hour2: `<strong>${endTime}</strong>`,
                  }}
                />
              </>
            }
            canClose={false}
            type="info-low"
          />
        )}
      </ModalContent>
    </Modal>
  );
};

export default ModifyDigitalPracticeModal;
